package xim.poc.game.configuration.v0.tactics

import xim.poc.browser.RemoteMetrics
import xim.poc.game.CombatStat
import xim.poc.game.CombatStats
import xim.poc.game.GameEngine.displayName
import xim.poc.game.JobLevel
import xim.poc.game.actor.components.InventoryItem
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.GameV0SaveStateHelper
import xim.poc.game.configuration.v0.TacticsInstinct
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.game.configuration.v0.UnitId
import xim.poc.game.configuration.v0.tactics.TacticsState.getLevelStatMultiplier
import xim.poc.game.configuration.v0.tactics.TacticsState.getMaxLevel
import xim.poc.game.configuration.v0.tactics.TacticsState.hasUnlockedRank
import xim.poc.game.toMultiplier
import xim.resource.MagicType
import xim.resource.table.SpellInfoTable.toSpellInfo
import kotlin.math.abs
import kotlin.math.pow
import kotlin.math.roundToInt

object TacticsState {

    private val state = GameV0SaveStateHelper.getState().tacticsState
    private var nextId = (getPlayerUnits().maxOfOrNull { it.id } ?: 0) + 1

    fun getPlayerUnits(): List<TacticsUnit> {
        return state.units
    }

    fun makeUnit(
        job: TacticsJobId,
        name: String = tacticsUnitNames.random(),
        level: JobLevel = JobLevel(level = 1, exp = 0),
    ): TacticsUnit {
        val unitLevel = JobLevel(exp = level.exp, level = level.level)
        return TacticsUnit(id = nextId++, name = name, currentJob = job, jobLevels = mutableMapOf(job to level), unitLevel = unitLevel)
    }

    fun addUnit(unit: TacticsUnit) {
        state.units += unit
    }

    fun getHirableJobs(): List<TacticsJobConfig> {
        val alreadyHiredFamilies = getPlayerUnits()
            .map { it.getCurrentJobConfig().group }
            .toSet()

        return TacticsJobGroup.values().mapNotNull { it.baseJob }
            .map { TacticsJobConfigs[it] }
            .filter { !alreadyHiredFamilies.contains(it.group) }
    }

    fun removeUnit(unit: TacticsUnit) {
        state.units.removeAll { it.id == unit.id }
    }

    fun reorder(unitToMoveId: UnitId, unitToMoveAboveId: UnitId) {
        if (unitToMoveId == unitToMoveAboveId) { return }

        val unitToMove = state.units.firstOrNull { it.id == unitToMoveId } ?: return
        state.units.remove(unitToMove)

        val idx = state.units.indexOfFirst { it.id == unitToMoveAboveId }
        if (idx >= 0) {
            state.units.add(idx, unitToMove)
        } else {
            state.units.add(unitToMove)
        }
    }

    fun onItemCleared(item: InventoryItem, match: TacticsMatchInstance) {
        RemoteMetrics.append("itemClear" to item.id.toString())
        emitUnitMetrics(match)
        state.itemsCleared += 1
    }

    fun hasClearedAnyItem(): Boolean {
        return state.itemsCleared > 0
    }

    fun isChallengeCleared(id: TacticsChallengeId): Boolean {
        return state.challengesCleared.contains(id)
    }

    fun onChallengeCleared(id: TacticsChallengeId, match: TacticsMatchInstance): Boolean {
        val cleared = state.challengesCleared
        if (cleared.contains(id)) { return false }

        cleared += id
        RemoteMetrics.append("challengeClear" to id.toString())
        emitUnitMetrics(match)
        return true
    }

    fun hasClearedChallenge(id: TacticsChallengeId): Boolean {
        return state.challengesCleared.contains(id)
    }

    fun shouldConfirmWait(): Boolean {
        return state.confirmWait
    }

    fun getLevelStatMultiplier(level: Float): Float {
        return 1.05f.pow(level - 1)
    }

    fun hasUnlockedJobChange(): Boolean {
        return hasClearedChallenge(tacticsChallenge_Frontier)
    }

    fun hasUnlockedRankUp(): Boolean {
        return hasClearedChallenge(tacticsChallenge_Frontier)
    }

    fun hasUnlockedInheritance(): Boolean {
        return hasClearedChallenge(tacticsChallenge_Emissary)
    }

    fun hasUnlockedIdleXp(): Boolean {
        return hasClearedChallenge(tacticsChallenge_BattaruRoyale)
    }

    fun hasUnlockedRank(statRank: StatRank): Boolean {
        return when (statRank) {
            StatRank.F -> true
            StatRank.E -> true
            StatRank.D -> true
            StatRank.C -> hasClearedChallenge(tacticsChallenge_Frontier)
            StatRank.B -> hasClearedChallenge(tacticsChallenge_Nought)
            StatRank.A -> false
            StatRank.S -> false
            StatRank.X -> false
        }
    }

    fun getMaxInstincts(): Int {
        return if (hasClearedChallenge(tacticsChallenge_AncientVows)) {
            4
        } else {
            3
        }
    }

    fun getMaxLevel(): Int {
        return 35
    }

    private fun emitUnitMetrics(matchInstance: TacticsMatchInstance) {
        val units = matchInstance.getSummonedUnitIds().mapNotNull { state.units.firstOrNull { u -> u.id == it } }
        units.forEachIndexed { index, unit ->
            RemoteMetrics.append(
                "uj${index}" to unit.currentJob.id.toString(),
                "ul${index}" to unit.unitLevel.level.toString(),
            )
        }
    }

}

private val tacticsBaseStats = CombatStats(40, 1000, 20, 20, 20, 20, 20, 20, 20)


fun TacticsUnit.getCurrentJobLevel() = jobLevels.getOrPut(currentJob) { JobLevel(exp = 0, level = 1) }
fun TacticsUnit.getCurrentJobConfig() = TacticsJobConfigs[currentJob]

fun TacticsUnit.getPowerRank(): StatRank {
    return StatRank.fromValue(unitRank) ?: throw IllegalStateException("No such stat rank: $unitRank")
}

fun TacticsUnit.rankUp(): Boolean {
    val result = validateRankUp()
    if (result !is RankUpSuccess) { return false }

    unitRank = result.nextRank.value
    unitLevel.level = 1
    unitLevel.exp = 0

    return true
}

fun TacticsUnit.validateRankUp(): RankUpResult {
    val currentRank = getPowerRank()
    val nextRank = currentRank.next()
    if (currentRank == nextRank) { return RankUpFailure("Reached max rank!") }

    if (!hasUnlockedRank(nextRank)) { return RankUpFailure("Clear more challenges to unlock Rank ${nextRank.value}!") }

    val nextLevelReq = ranksByLevel[nextRank] ?: return RankUpFailure("Reached max rank!")
    if (unitLevel.level < nextLevelReq) { return RankUpFailure("Level is too low! Needed: Lv.$nextLevelReq", nextLevelReq) }

    return RankUpSuccess(nextRank)
}

fun TacticsUnit.getCombatStatRanks(jobId: TacticsJobId = currentJob): CombatStatRanks {
    val jobConfig = TacticsJobConfigs[jobId]
    val jobPowerRank = getPowerRank()
    return jobConfig.stats.offset(jobPowerRank.value - StatRank.D.value)
}

fun TacticsUnit.getWeaponPower(jobId: TacticsJobId = currentJob): Int {
    val jobLevel = jobLevels[jobId]?.level ?: 1
    val jobRank = getPowerRank()

    val effectiveLevel = (jobLevel + unitLevel.level)/2f
    val levelMultiplier = getLevelStatMultiplier(effectiveLevel)

    val baseDamage = 50f * jobRank.weaponPower * levelMultiplier
    return baseDamage.roundToInt()
}

fun TacticsUnit.getCombatStats(jobId: TacticsJobId = currentJob): CombatStats {
    val jobLevel = jobLevels[jobId]?.level ?: 1
    val jobStats = getCombatStatRanks(jobId)

    val effectiveLevel = (jobLevel + unitLevel.level)/2f
    val levelMultiplier = getLevelStatMultiplier(effectiveLevel)

    val hpInstinct = getInstinctPotency(TacticsInstincts.maxHp)

    val stats = tacticsBaseStats * levelMultiplier
    return stats.multiply(jobStats.multipliers).multiply(CombatStat.maxHp, hpInstinct.toMultiplier())
}

fun TacticsUnit.unlockJobs(): Pair<List<TacticsJobId>, List<TacticsJobId>> {
    val newJobs = ArrayList<TacticsJobId>()
    val missedJobs = ArrayList<TacticsJobId>()

    val currentGroup = getCurrentJobConfig().group
    val validJobs = TacticsJobConfigs.getAll().filter { it.group == currentGroup }

    for (jobConfig in validJobs) {
        if (jobLevels.contains(jobConfig.id)) { continue }
        if (!hasUnlockedRank(jobConfig.minimumRank) || !jobConfig.playable) { continue }

        if (jobConfig.evolveRequirements.isEmpty()) {
            newJobs += jobConfig.id
            continue
        }

        if (jobConfig.evolveRequirements.all { it.check(this) }) {
            newJobs += jobConfig.id
        } else {
            missedJobs += jobConfig.id
        }
    }

    for (newJob in newJobs) {
        jobLevels[newJob] = JobLevel(level = 1, exp = 0)
    }

    return newJobs to missedJobs
}

fun TacticsUnit.computeSkills(): List<SkillId> {
    val selfSkills = computeSelfSkills()

    removeRedundantInheritance(selfSkills)
    val allSkills = selfSkills + inheritance.filter { it != mskillNull_0 }

    val overwrittenSkills = allSkills.mapNotNull { TacticsSkills[it].overwrites }.toSet()
    return allSkills.filter { !overwrittenSkills.contains(it) }.toList().sortedWith(::skillSorter)
}

private fun skillSorter(a: SkillId, b: SkillId): Int {
    return if (a is MobSkillId && b is MobSkillId) {
        0
    } else if (a is SpellSkillId && b is SpellSkillId) {
        a.id.compareTo(b.id)
    } else if (a is MobSkillId && b !is MobSkillId) {
        -1
    } else if (b is MobSkillId && a !is MobSkillId) {
        1
    } else if (a is AbilitySkillId && b is SpellSkillId) {
        -1
    } else if (b is AbilitySkillId && a is SpellSkillId) {
        1
    } else {
        a.displayName().compareTo(b.displayName())
    }
}

fun TacticsUnit.computeSelfSkills(): Set<SkillId> {
    val allSkills = LinkedHashSet<SkillId>()
    allSkills += getCurrentJobConfig().skillsByLevel
        .filter { it.key <= getCurrentJobLevel().level }
        .values.flatten()

    for ((jobId, jobLevel) in jobLevels) {
        if (jobId == currentJob) { continue }

        allSkills += TacticsJobConfigs[jobId].skillsByLevel.filter { it.key <= jobLevel.level }.values
            .flatten()
            .filter { TacticsSkills[it].crossJob != null }
    }

    allSkills += instincts.mapNotNull { TacticsInstincts[it.id].skillUnlock }
    return allSkills
}

fun TacticsUnit.getSkillRange(skillId: SkillId): Pair<Int, Int> {
    val config = TacticsSkills[skillId]

    val bonusRange = if (skillId is SpellSkillId) {
        val info = skillId.toSpellInfo()
        when (info.magicType) {
            MagicType.WhiteMagic -> getInstinctPotency(TacticsInstincts.whiteMagicRange) + getInstinctPotency(TacticsInstincts.magicRange)
            MagicType.BlackMagic -> getInstinctPotency(TacticsInstincts.blackMagicRange) + getInstinctPotency(TacticsInstincts.magicRange)
            else -> 0
        }
    } else {
        0
    }

    return config.minRange to config.maxRange + (bonusRange ?: 0)
}

fun TacticsUnit.getSkillChargeCount(skillId: SkillId): Int? {
    val config = TacticsSkills[skillId]
    if (config.charges == null) { return null }

    val divisor = if (isSkillInherited(skillId)) { 2 } else { 1 }
    return config.charges / divisor
}

fun TacticsUnit.isSkillInherited(skillId: SkillId): Boolean {
    return inheritance.any { it == skillId }
}

fun TacticsUnit.addInstinct(definition: TacticsInstinctDefinition, potency: Int): TacticsUnit {
    instincts += TacticsInstinct(definition.id, potency)
    return this
}

fun TacticsUnit.getInstinctPotency(definition: TacticsInstinctDefinition): Int {
    return instincts.firstOrNull { it.id == definition.id }?.potency ?: 0
}

fun TacticsUnit.getMovementRange(): Int {
    return getCurrentJobConfig().movement + instincts.sumOf { TacticsInstincts[it.id].movement ?: 0 }
}

fun TacticsUnit.isMaxUnitLevel(): Boolean {
    return unitLevel.level == getMaxLevel()
}

fun TacticsUnit.isMaxJobLevel(jobId: TacticsJobId = currentJob): Boolean {
    return jobLevels[jobId]?.level == getMaxLevel()
}

private fun TacticsUnit.removeRedundantInheritance(skills: Set<SkillId>) {
    for (i in inheritance.indices) {
        if (skills.contains(inheritance[i])) {
            inheritance[i] = mskillNull_0
        }
    }
}

fun TacticsUnit.gainExp(target: TacticsUnit, multiplier: Float = 1f): GainXpResult {
    val unitExpGained = computeXpGained(target, multiplier)
    val jobExpGained = computeJpGained(target, multiplier)

    val unitLevelGained = applyXp(unitExpGained, unitLevel)
    val jobLevelGained = applyXp(jobExpGained, getCurrentJobLevel())

    highestLevel = maxOf(highestLevel, unitLevel.level)
    if (jobLevelGained) { unlockJobs() }

    return GainXpResult(
        xpGained = unitExpGained,
        jpGained = jobExpGained,
        unitLevelGained = unitLevelGained,
        jobLevelGained = jobLevelGained,
    )
}

private fun TacticsUnit.computeXpGained(defeated: TacticsUnit, multiplier: Float): Int {
    if (isMaxUnitLevel() && unitLevel.exp == 99) { return 0 }
    val base = computeExpGained(defeated, unitLevel, multiplier)

    val levelDelta = highestLevel - unitLevel.level
    if (levelDelta <= 0) { return base }

    return (base * (1f + levelDelta * 0.1f)).roundToInt()
}

private fun TacticsUnit.computeJpGained(defeated: TacticsUnit, multiplier: Float): Int {
    if (isMaxJobLevel() && getCurrentJobLevel().exp == 99) { return 0 }
    val base = computeExpGained(defeated, getCurrentJobLevel(), multiplier)

    val levelDelta = highestLevel - getCurrentJobLevel().level
    if (levelDelta <= 0) { return base }

    return (base * (1f + levelDelta * 0.1f)).roundToInt()
}

private fun computeExpGained(defeated: TacticsUnit, actorLevel: JobLevel, multiplier: Float): Int {
    val levelDelta = defeated.unitLevel.level - actorLevel.level

    val lowLevelBonus = 1f + (1.0f - actorLevel.level * 0.2f).coerceAtLeast(0f)

    return (lowLevelBonus * multiplier * if (levelDelta >= 0) {
        10.0 + 5 * levelDelta
    } else {
        (10.0 - 2.0 * abs(levelDelta)).coerceAtLeast(0.0)
    }).roundToInt()
}

private fun applyXp(amount: Int, jobLevel: JobLevel): Boolean {
    jobLevel.exp += amount
    if (jobLevel.exp < 100) { return false }

    if (jobLevel.level == getMaxLevel()) {
        jobLevel.exp = 99
        return false
    }

    while (jobLevel.exp >= 100) {
        jobLevel.level += 1
        jobLevel.exp -= 100
    }

    return true
}

data class GainXpResult(
    val xpGained: Int,
    val jpGained: Int,
    val unitLevelGained: Boolean,
    val jobLevelGained: Boolean,
)

sealed interface RankUpResult
class RankUpFailure(val reason: String, val requiredLevel: Int? = null): RankUpResult
class RankUpSuccess(val nextRank: StatRank): RankUpResult

private val ranksByLevel = mapOf(
    StatRank.F to 0,
    StatRank.E to 0,
    StatRank.D to 1,
    StatRank.C to 10,
    StatRank.B to 25,
    StatRank.A to 40,
    StatRank.S to 70,
)
